import { connect } from 'react-redux';
import { useMemo } from 'react';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmDataCreated,
  getKnmErknmParticipantPositionsSelectOptions,
  getKnmErknmPositionsSelectOptions,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import {
  getDecisionParticipantPositionsSelectOptions,
  getDecisionPositionsSelectOptions,
  getDecisionDataCreatedAt,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IDictionaryParticipantPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryParticipantPositionSelectOption';
import { IDictionaryPositionSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryPositionSelectOption';
import { Hint } from 'CorePackage/MainModule/components/Hint';

import styles from './ResultDecisionsItemContentSignInfo.module.scss';
import { IKnmFormRef } from '../../../../../../KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IDecisionFormRef } from '../../../../../../DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { lodashGet } from '../../../../../../functions/lodashGet';
import { ProfKindCodesEnum } from '../../../../../../APIPackage/DictionariesModule/enums/ProfKindCodesEnum';
import { ExternalSystemSignInfoBlock } from '../../../../../../CorePackage/MainModule/components/ExternalSystemSignInfoBlock';
import { IAdminSetting } from '../../../../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { getAdminFetchSettings } from '../../../../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { isLaterThanAdminSetting } from '../../../../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { Signers } from '../../../../../../KnmPackage/SignersModule/components/Signers';
import { getResultDecisionPositionsSelectOptions } from '../../../../../../functions/getResultDecisionPositionsSelectOptions';
import { KindResultDecisionCodesEnum } from '../../../../../../APIPackage/CatalogsModule/enums/KindResultDecisionCodesEnum';
import { getLawInterface } from '../../../../../../CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from '../../../../../../CorePackage/MainModule/enums/LawInterfacesEnum';

interface IResultDecisionsItemContentSignInfoProps {
  fioSignerFieldName: string;
  fioSignerId: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  hasResultDecisionFirstExtractDateTime: boolean;
  isDecision?: boolean;
  isDisabledField: boolean;
  isKnm?: boolean;
  isNew: boolean;
  isPm?: boolean;
  isSigned: boolean | undefined;
  kindCode: KindResultDecisionCodesEnum | undefined;
  name: string;
  resultDecisionId: string;
  resultDecisionKindCode: KindResultDecisionCodesEnum | undefined;
  titleSignerFieldName: string;
  titleSignerId: string;
}

interface IResultDecisionsItemContentSignInfoStoreProps {
  created: string | null | undefined;
  isErp: boolean;
  participantPositionsSelectOptions: IDictionaryParticipantPositionSelectOption[] | undefined;
  positionsSelectOptions: IDictionaryPositionSelectOption[] | undefined;
  settings: IAdminSetting[] | undefined;
}

const ResultDecisionsItemContentSignInfoWithoutStore = ({
  fioSignerFieldName,
  fioSignerId,
  formRef,
  hasResultDecisionFirstExtractDateTime,
  isDecision,
  isDisabledField,
  isKnm,
  isNew,
  isPm,
  isSigned,
  kindCode,
  name,
  resultDecisionId,
  resultDecisionKindCode,
  titleSignerFieldName,
  titleSignerId,
  created,
  isErp,
  participantPositionsSelectOptions,
  positionsSelectOptions,
  settings,
}: IResultDecisionsItemContentSignInfoProps & IResultDecisionsItemContentSignInfoStoreProps) => {
  // @ts-ignore
  const formValues = formRef?.current.getState().values;
  const decisionMeta = lodashGet(formValues, name, {});
  // @ts-ignore
  const isTypePP = isPm ? formRef?.current.getFieldState('pmType')?.value?.code === ProfKindCodesEnum.PP : false;

  const isUseSigners = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ADD_NEW_SIGNER_199_4_1_6_STARTS_FROM,
  );

  const fullPositionsSelectOptions = useMemo(
    () => getResultDecisionPositionsSelectOptions(kindCode, participantPositionsSelectOptions, positionsSelectOptions),
    [kindCode, participantPositionsSelectOptions, positionsSelectOptions],
  );

  const isSigners =
    isUseSigners &&
    (resultDecisionKindCode === KindResultDecisionCodesEnum.D_1 ||
      resultDecisionKindCode === KindResultDecisionCodesEnum.D_7);

  let content;

  if (isSigners && !isErp) {
    content = (
      <Signers
        formRef={formRef}
        hasActOrResultDecisionFirstExtractDateTime={hasResultDecisionFirstExtractDateTime}
        hintText="Введите ФИО и должность лица, уполномоченного на принятие решения"
        inspectors={undefined}
        isActOrResultDecisionSigned={isSigned === true}
        isDecision={isDecision === true}
        isKnm={isKnm === true}
        isNewActOrResultDecision={isNew}
        isPm={isPm === true}
        label="Подпись предписания"
        parentId={resultDecisionId}
        parentName={name}
        positionsSelectOptions={fullPositionsSelectOptions}
      />
    );
  } else if (
    ((isKnm || isDecision) && decisionMeta?.systemSignatureUsed) ||
    (isPm && isTypePP && decisionMeta?.systemSignatureUsed)
  ) {
    content = (
      <ExternalSystemSignInfoBlock
        systemSignatureDateTime={decisionMeta.systemSignatureDateTime}
        systemSignatureTitle={decisionMeta.systemSignatureTitle}
      />
    );
  } else {
    content = (
      <>
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label: 'ФИО подписавшего решение',
            field: fioSignerFieldName,
            isDecision,
            isKnm,
            isPm,
          })}
          color="black"
          className={styles.FieldBlock}
          placementLabel="left"
          margin="bottom"
          id={fioSignerId}
        >
          <TextInputField name={fioSignerFieldName} disabled={isDisabledField} />
        </FieldBlock>
        <FieldBlock
          label={getTitleFieldWithRedStar({
            label: 'Должность лица, подписавшего решение',
            field: titleSignerFieldName,
            isDecision,
            isKnm,
            isPm,
          })}
          color="black"
          className={styles.FieldBlock}
          placementLabel="left"
          id={titleSignerId}
          margin="bottom"
        >
          <SelectInputField
            name={titleSignerFieldName}
            placeholder="Выберите должность"
            options={fullPositionsSelectOptions}
            isSearchable
            disabled={isDisabledField}
            size="medium"
          />
        </FieldBlock>
        <Hint text="Введите ФИО и должность лица, уполномоченного на принятие решения" />
      </>
    );
  }

  return <FieldBlock label={isSigners ? '' : 'Подпись предписания'}>{content}</FieldBlock>;
};

const mapStateToProps = (
  state: IRootState,
  { isDecision }: IResultDecisionsItemContentSignInfoProps,
): IResultDecisionsItemContentSignInfoStoreProps => ({
  created: isDecision === true ? getDecisionDataCreatedAt(state) : getKnmDataCreated(state),
  isErp: getLawInterface(state) !== LawInterfacesEnum.FZ_248,
  participantPositionsSelectOptions:
    isDecision === true
      ? getDecisionParticipantPositionsSelectOptions(state)
      : getKnmErknmParticipantPositionsSelectOptions(state),
  positionsSelectOptions:
    isDecision === true ? getDecisionPositionsSelectOptions(state) : getKnmErknmPositionsSelectOptions(state),
  settings: getAdminFetchSettings(state),
});

export const ResultDecisionsItemContentSignInfo = connect(mapStateToProps)(
  ResultDecisionsItemContentSignInfoWithoutStore,
);
